/**
 * Normalizes a date coming off the API to an ISO-ish string.
 *
 * Most `DateTime?` fields arrive as ISO strings, but properties typed as the `LedgerVue.Core.Date`
 * value object (notably `DecimalValueDto.EffectiveDate`) are serialized as
 * `{ dateTime, year, month, day, ... }`. Treating those as strings yields an invalid date, which is
 * why price and FX rate dates need this normalization before formatting.
 */
export function dateStringOrNull(raw: unknown): string | null {
  if (typeof raw === 'string') {
    return raw.trim() ? raw.trim() : null;
  }
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }

  const o = raw as Record<string, unknown>;
  const dateTime = o.dateTime ?? o.DateTime;
  if (typeof dateTime === 'string' && dateTime.trim()) {
    return dateTime.trim();
  }

  const year = o.year ?? o.Year;
  const month = o.month ?? o.Month;
  const day = o.day ?? o.Day;
  if (typeof year !== 'number' || typeof month !== 'number' || typeof day !== 'number') {
    return null;
  }
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function toDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const iso = dateStringOrNull(value);
  if (!iso) return null;
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatNumber(value: number | null | undefined, decimals = 2): string {
  if (value == null) return '—';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function formatMoney(value: number | null | undefined): string {
  return formatNumber(value, 2);
}

/** Compact currency for KPI tiles (e.g. 8M, 4.9M, 600K). */
export function formatCompactMoney(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const sign = value < 0 ? '-' : '';
  const v = Math.abs(value);
  if (v >= 1_000_000_000) {
    const x = v / 1_000_000_000;
    const s = x >= 10 ? x.toFixed(0) : x.toFixed(1).replace(/\.0$/, '');
    return `${sign}${s}B`;
  }
  if (v >= 1_000_000) {
    const x = v / 1_000_000;
    const s = x >= 10 ? x.toFixed(0) : x.toFixed(1).replace(/\.0$/, '');
    return `${sign}${s}M`;
  }
  if (v >= 1_000) {
    return `${sign}${Math.round(v / 1_000)}K`;
  }
  return sign + formatNumber(v, 0);
}

export function formatDateShort(value: unknown): string {
  const date = toDate(value);
  if (!date) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = String(date.getFullYear()).slice(-2);
  return `${day}-${month}-${year}`;
}

/** Format a decimal ratio as a percentage (e.g., 0.0532 → "+5.32%"). */
export function formatPercent(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const pct = value * 100;
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
}
